"use client";

import { Activity } from "lucide-react";
import {
  ActivitySessionCourtPreview,
  isActivitySessionCourtPreviewSupported,
  shouldUseActivitySessionCourtPreview,
  type TeamParticipant,
} from "@/components/activity-sessions/session-court-preview";
import {
  ActivityDisplayPreview,
  hasActivityDisplayPreview,
} from "@/components/posts/activity-templates/previews/activity-display-preview";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { ActivitySessionFormat } from "@/db/schema/activities";
import { getIconComponent, type IconType } from "@/lib/icon-registry";

type SessionSceneProps = {
  activityId: string;
  activityIcon?: string | null;
  sessionFormat: ActivitySessionFormat | null;
  scoreData?: Record<string, unknown> | null;
  participants: TeamParticipant[];
};

/**
 * The illustrated scene every session card leads with — the most eye-catching
 * part of the card, so EVERY session gets one:
 * - a court/field sport → the court with both sides lined up (and the score
 *   once there is one). A "group" session on a court sport has no teams, so
 *   the players are split across the net — doubles up to four, teams beyond —
 *   rather than leaving an empty court that says "waiting";
 * - an individual activity with an illustrated scene → that scene;
 * - an activity without one → a generic stage built from its icon.
 *
 * Scenes draw their own rounded, bordered, shadowed frame for standalone use;
 * inside a card that frame doubles up, so it is flattened here and the card's
 * own frame is the only one.
 */
export function SessionScene({
  activityId,
  activityIcon,
  sessionFormat,
  scoreData = null,
  participants,
}: SessionSceneProps) {
  return (
    <div className="[&_.rounded-3xl.select-none]:rounded-none [&_.rounded-3xl.select-none]:border-0 [&_.rounded-3xl.select-none]:shadow-none">
      <SceneBody
        activityId={activityId}
        activityIcon={activityIcon}
        sessionFormat={sessionFormat}
        scoreData={scoreData}
        participants={participants}
      />
    </div>
  );
}

function SceneBody({
  activityId,
  activityIcon,
  sessionFormat,
  scoreData,
  participants,
}: SessionSceneProps) {
  const isCourtSport =
    isActivitySessionCourtPreviewSupported(activityId) &&
    // Individual activities (yoga, hyrox, golf…) report true for any format.
    !shouldUseActivitySessionCourtPreview(activityId, "group");

  if (isCourtSport) {
    const isMatch = sessionFormat != null && sessionFormat !== "group";
    return (
      <ActivitySessionCourtPreview
        activityId={activityId}
        sessionFormat={
          isMatch
            ? sessionFormat
            : participants.length <= 2
              ? "singles"
              : participants.length <= 4
                ? "doubles"
                : "team"
        }
        scoreData={scoreData}
        participants={isMatch ? participants : splitAcrossNet(participants)}
      />
    );
  }

  if (hasActivityDisplayPreview(activityId)) {
    return (
      <ActivityDisplayPreview
        activityId={activityId}
        data={scoreData}
        participants={participants}
      />
    );
  }

  return (
    <GenericActivityScene
      activityIcon={activityIcon}
      participants={participants}
    />
  );
}

/** Alternate unassigned players between the two sides. */
function splitAcrossNet(participants: TeamParticipant[]): TeamParticipant[] {
  return participants.map((participant, index) => ({
    ...participant,
    team: participant.team ?? (index % 2 === 0 ? "team1" : "team2"),
  }));
}

function GenericActivityScene({
  activityIcon,
  participants,
}: {
  activityIcon?: string | null;
  participants: TeamParticipant[];
}) {
  const Icon = activityIcon ? getIconComponent(activityIcon as IconType) : null;
  return (
    <div className="relative flex h-[200px] flex-col items-center justify-center overflow-hidden bg-surface-alt bg-[radial-gradient(120%_90%_at_50%_0%,color-mix(in_oklab,var(--color-brand)_28%,transparent),transparent_70%)] dark:bg-[#0c0a14]">
      <span className="pointer-events-none absolute -right-6 -bottom-8 text-brand/10 dark:text-white/[0.06] [&_svg]:size-40">
        {Icon ? <Icon /> : <Activity />}
      </span>
      <span className="flex size-16 items-center justify-center rounded-2xl bg-background/80 text-brand shadow-sm ring-1 ring-border backdrop-blur dark:bg-white/[0.06] dark:text-violet-200 dark:ring-white/10 [&_svg]:size-9">
        {Icon ? <Icon /> : <Activity />}
      </span>
      {participants.length > 0 ? (
        <span className="mt-4 flex -space-x-2">
          {participants.slice(0, 5).map((p, i) => (
            <Avatar
              key={p.id}
              className="size-8 border-2 border-background dark:border-[#0c0a14]"
              style={{ zIndex: 10 - i }}
            >
              <AvatarImage src={p.avatarUrl ?? undefined} />
              <AvatarFallback className="rounded-none bg-surface-alt text-[10px]">
                {p.displayName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ))}
        </span>
      ) : null}
    </div>
  );
}
